import { APIConfiguration } from '@savgroup-front-common/configuration';
import {
  COMMON_QUERY_PARAMS,
  COUNTRY_CODES,
  SUPPORTED_METHODS,
} from '@savgroup-front-common/constants';
import { TransportMethodType } from '@savgroup-front-common/core/api/Claim/getClaimGroupTransportPlanQuery';
import { prepareResponseFailure } from '@savgroup-front-common/core/helpers';
import { apiCall } from '@savgroup-front-common/core/services';
import {
  BackResponse,
  BackResponseFailure,
  CUSTOMER_TYPE,
  ModelDimension,
  ProductStates,
  ReverseMoney,
} from '@savgroup-front-common/types';

const ENDPOINT = `${APIConfiguration.claim}debugging/claims/${COMMON_QUERY_PARAMS.CLAIM_ID}/context`;

export const ACTOR_TYPE = {
  Accountant: 'Accountant',
  Data: 'Data',
  DeliveryStore: 'DeliveryStore',
  DepositStore: 'DepositStore',
  ImplicitSupplier: 'ImplicitSupplier',
  Insurer: 'Insurer',
  Order: 'Order',
  Owner: 'Owner',
  PurchaseStore: 'PurchaseStore',
  Repairer: 'Repairer',
  RiskCarrier: 'RiskCarrier',
  Seller: 'Seller',
  Site: 'Site',
  Store: 'Store',
  Supplier: 'Supplier',
  Technical: 'Technical',
  Van: 'Van',
  Warehouse: 'Warehouse',
} as const;

export type ActorType = (typeof ACTOR_TYPE)[keyof typeof ACTOR_TYPE];

export const ORDER_PAYMENT_METHOD = {
  Alma: 'Alma',
  Cetelem: 'Cetelem',
  CreditCard: 'CreditCard',
  CreditNote: 'CreditNote',
  ExchangeVoucher: 'ExchangeVoucher',
  GiftCard: 'GiftCard',
  Klarna: 'Klarna',
  Mixed: 'Mixed',
  Monext: 'Monext',
  Others: 'Others',
  PayPal: 'PayPal',
  PayPlug: 'PayPlug',
  Primer: 'Primer',
  Purse: 'Purse',
  Sequra: 'Sequra',
  Stripe: 'Stripe',
  Voucher: 'Voucher',
  WireTransfer: 'WireTransfer',
} as const;

export type OrderPaymentMethod =
  (typeof ORDER_PAYMENT_METHOD)[keyof typeof ORDER_PAYMENT_METHOD];

export const ORDER_DELIVERY_METHOD = {
  ClickAndCollect: 'ClickAndCollect',
  HomeDelivery: 'HomeDelivery',
  InStorePickUpDelivery: 'InStorePickUpDelivery',
  LocalPickUpDelivery: 'LocalPickUpDelivery',
  NotSet: 'NotSet',
  ReserveAndCollect: 'ReserveAndCollect',
  ScheduledDoorstepDelivery: 'ScheduledDoorstepDelivery',
  ScheduledInHomeDelivery: 'ScheduledInHomeDelivery',
  TakenAway: 'TakenAway',
} as const;

export type OrderDeliveryMethod =
  (typeof ORDER_DELIVERY_METHOD)[keyof typeof ORDER_DELIVERY_METHOD];

export const ORDER_DELIVERY_STATUS = {
  BeingPrepared: 'BeingPrepared',
  BeingShipped: 'BeingShipped',
  Cancelled: 'Cancelled',
  Collected: 'Collected',
  Delivered: 'Delivered',
  NotSet: 'NotSet',
  Ordered: 'Ordered',
} as const;

export type OrderDeliveryStatus =
  (typeof ORDER_DELIVERY_STATUS)[keyof typeof ORDER_DELIVERY_STATUS];

export const ORDER_COMMERCIAL_DOCUMENT_TYPE = {
  DeliveryNote: 'DeliveryNote',
  Order: 'Order',
  SalesReceipt: 'SalesReceipt',
} as const;

export type OrderCommercialDocumentType =
  (typeof ORDER_COMMERCIAL_DOCUMENT_TYPE)[keyof typeof ORDER_COMMERCIAL_DOCUMENT_TYPE];

export const TRANSPORT_ACTOR_TYPE = {
  CurrentActor: 'CurrentActor',
  Default: 'Default',
  DeliveryStore: 'DeliveryStore',
  DepositStore: 'DepositStore',
  ImplicitSupplier: 'ImplicitSupplier',
  Owner: 'Owner',
  PurchaseStore: 'PurchaseStore',
  Repairer: 'Repairer',
  Seller: 'Seller',
  SiteActor: 'SiteActor',
  Store: 'Store',
  Supplier: 'Supplier',
  Warehouse: 'Warehouse',
} as const;

export type TransportActorType =
  (typeof TRANSPORT_ACTOR_TYPE)[keyof typeof TRANSPORT_ACTOR_TYPE];

export const HANDLING_PROCEDURE = {
  Destruction: 'Destruction',
  Transport: 'Transport',
} as const;

export type HandlingProcedure =
  (typeof HANDLING_PROCEDURE)[keyof typeof HANDLING_PROCEDURE];

export const CLAIM_WORKFLOW_STATE = {
  Closed: 'Closed',
  ClosedForRma: 'ClosedForRma',
  Confirmation: 'Confirmation',
  Created: 'Created',
  Summary: 'Summary',
  WaitingForDiagnosticConfirmation: 'WaitingForDiagnosticConfirmation',
  WaitingForHandlingDeliveryInfo: 'WaitingForHandlingDeliveryInfo',
  WaitingForHandlingDepositInfo: 'WaitingForHandlingDepositInfo',
  WaitingForHandlingMode: 'WaitingForHandlingMode',
  WaitingForOnSiteInterventionInfo: 'WaitingForOnSiteInterventionInfo',
  WaitingForReason: 'WaitingForReason',
  WaitingForSolution: 'WaitingForSolution',
} as const;

export type ClaimWorkflowState =
  (typeof CLAIM_WORKFLOW_STATE)[keyof typeof CLAIM_WORKFLOW_STATE];

export const CARRIERS_ENUM = {
  Chronopost: 'Chronopost',
  Colissimo: 'Colissimo',
  DPD: 'DPD',
  External: 'External',
  Geodis: 'Geodis',
  GLS: 'GLS',
  MondialRelay: 'MondialRelay',
  SwissPost: 'SwissPost',
  Teliae: 'Teliae',
  UPS: 'UPS',
  Vir: 'Vir',
  Warning: 'Warning',
} as const;

export type CarriersEnum = (typeof CARRIERS_ENUM)[keyof typeof CARRIERS_ENUM];

export const CARRIER_DIMENSION_TYPE = {
  XL: 'XL',
  XS: 'XS',
} as const;

export type CarrierDimensionType =
  (typeof CARRIER_DIMENSION_TYPE)[keyof typeof CARRIER_DIMENSION_TYPE];

export const REFUND_POLICY_TYPE = {
  CommercialGesture: 'CommercialGesture',
  NotSet: 'NotSet',
  Refund: 'Refund',
} as const;

export type RefundPolicyType =
  (typeof REFUND_POLICY_TYPE)[keyof typeof REFUND_POLICY_TYPE];

export const MARKETING_OFFER_TYPE = {
  CashRegister: 'CashRegister',
  Check: 'Check',
  CreditNote: 'CreditNote',
  DigitalGiftCard: 'DigitalGiftCard',
  ExchangeOnSellerStock: 'ExchangeOnSellerStock',
  FranchisedCashRegister: 'FranchisedCashRegister',
  GiftCard: 'GiftCard',
  Lender: 'Lender',
  LoyaltyCard: 'LoyaltyCard',
  NotSet: 'NotSet',
  OtherRefund: 'OtherRefund',
  PaymentService: 'PaymentService',
  RefundByWireTransfer: 'RefundByWireTransfer',
  RefundOnPaymentMethod: 'RefundOnPaymentMethod',
  Voucher: 'Voucher',
} as const;

export type MarketingOfferType =
  (typeof MARKETING_OFFER_TYPE)[keyof typeof MARKETING_OFFER_TYPE];

export const ITEMS_TO_REFUND = {
  DeductibleHandlingPrice: 'DeductibleHandlingPrice',
  None: 'None',
  Products: 'Products',
  Services: 'Services',
  ShippingOption: 'ShippingOption',
  ShippingPrice: 'ShippingPrice',
  SoldWarranties: 'SoldWarranties',
} as const;

export type ItemsToRefund =
  (typeof ITEMS_TO_REFUND)[keyof typeof ITEMS_TO_REFUND];

export const PAYMENT_PROVIDER = {
  Alma: 'Alma',
  Custom: 'Custom',
  Monext: 'Monext',
  None: 'None',
  PayPal: 'PayPal',
  PayPlug: 'PayPlug',
  Purse: 'Purse',
  Stripe: 'Stripe',
} as const;

export type PaymentProvider =
  (typeof PAYMENT_PROVIDER)[keyof typeof PAYMENT_PROVIDER];

export interface SoldWarrantyDetailsSummary {
  warrantyId?: string;
  warrantyTypeId?: string;
  soldWarrantyName?: string;
  soldWarrantyUniqueName?: string;
  soldWarrantyPrice?: ReverseMoney;
  purchaseDate?: string;
  insurerUniqueName?: string;
  insurerId: string;
}

export interface TransportPlanDimensionSpecifications {
  carrier?: CarriersEnum;
  type?: CarrierDimensionType;
  maxVolume?: string;
  minWeightInKg: number;
  maxWeightInKg: number;
}

export interface RefundPolicyDetails {
  refundType?: RefundPolicyType;
  isRefundAutomated: boolean;
  refundMethod?: MarketingOfferType;
  itemsToRefund?: ItemsToRefund[];
  limitAmount?: number;
  limitAmountByPercentage?: number;
  isHandlingDeductedOnFirstFileOnly?: boolean;
}

export interface KeyValues {
  key?: string;
  values?: string[];
}

export interface ClaimDebuggingContext {
  sellerId: string;
  claimId: string;
  orderLineReference?: string;
  orderReference?: string;
  orderId?: string;
  fileReference?: string;
  fileId?: string;
  ownerProductId: string;
  previousFileActorType?: ActorType;
  previousFileActorId?: string;
  ownerId: string;
  ownerExternalId?: string;
  ownerLoyaltyCardReference?: string;
  claimCreationDateUtc: string;
  claimOrigin?: string;
  salesChannel?: string;
  purchaseStoreId?: string;
  purchaseStoreName?: string;
  purchaseCountry?: string;
  country?: COUNTRY_CODES;
  zipCode?: string;
  recipientCountryCode?: string;
  recipientZipCode?: string;
  orderPaymentMethod?: OrderPaymentMethod;
  orderPurchaseDate?: string;
  orderDeliveryDate?: string;
  orderDeliveryMethod?: OrderDeliveryMethod;
  orderDeliveryStatus?: OrderDeliveryStatus;
  orderDocumentType?: OrderCommercialDocumentType;
  orderShippingPrice?: ReverseMoney;
  customerType?: CUSTOMER_TYPE;
  soldWarranty?: SoldWarrantyDetailsSummary;
  warrantyTypeId?: string;
  warrantyTypeName?: string;
  soldWarrantyId?: string;
  soldWarrantyPurchaseDate?: string;
  issueId?: string;
  issueName?: string;
  issueUniqueName?: string;
  reasonId?: string;
  reasonName?: string;
  solutionTypeId?: string;
  solutionTypeName?: string;
  solutionTypeUniqueName?: string;
  solutionGroup?: string;
  actorId?: string;
  actorName?: string;
  senderActorType?: TransportActorType;
  recipientActorType?: TransportActorType;
  recipientActorUniqueName?: string;

  depositTransportPlanMethodType?: TransportMethodType;
  depositTransportMethodId?: string;
  depositTransportMethodName?: string;
  depositTransportPlanId?: string;
  depositTransportPlanUniqueName?: string;

  deliveryTransportPlanMethodType?: TransportMethodType;
  deliveryTransportMethodId?: string;
  deliveryTransportMethodName?: string;
  deliveryTransportPlanId?: string;
  deliveryTransportPlanUniqueName?: string;

  depositTransportPlanDimensionSpecifications?: TransportPlanDimensionSpecifications[];
  carrierDeliveryProductId?: string;
  carrierDeliveryProductName?: string;
  deliveryTransportPlanDimensionSpecifications?: TransportPlanDimensionSpecifications[];

  carrierDepositProductId?: string;
  carrierDepositProductName?: string;
  depositCarrierName?: string;

  transportPlanHandlingProcedure?: HandlingProcedure;
  useTransportPlan?: boolean;
  claimWorkflowState?: ClaimWorkflowState;
  productModelTypeId: string;
  productModelTypeName?: string;
  productModelTypeHierarchy?: string[];
  productModelTypeHierarchySecondaryScoring?: Record<string, number>;
  productModelId: string;
  productModelName?: string;
  productBrandId: string;
  productBrandName?: string;
  productEan?: string;
  productSku?: string;
  productSupplierId?: string;
  productSupplierUniqueName?: string;
  productState?: ProductStates;
  productPrice?: ReverseMoney;
  productIsLowValue?: boolean;
  productWeight?: number;
  productDimensions?: ModelDimension;
  productIsRepairable?: boolean;
  productPictureUrl?: string;
  productIsCumbersome?: boolean;
  isAllReturned?: boolean;
  storeActorZipCode?: string;
  storeActorCountryCode?: string;
  ownerZipCode?: string;
  ownerCountry?: string;
  workflowState?: ClaimWorkflowState;
  countRepairs: number;
  refundPolicy?: RefundPolicyDetails;
  currentUserRoleNames?: string[];
  currentSiteId?: string;
  currentSiteCountryCode?: string;
  isOwnerAnonymous?: boolean;
  productCustomFields?: KeyValues[];
  storeBusinessModel?: string;
  storeLegalEntityExternalId?: string;
  hasLoyaltyCard?: boolean;
  loyaltyProgramNames?: string[];
  orderSoldServices?: string[];
  hasSoldWarranty?: boolean;
  soldWarrantyUniqueNames?: string[];
  storeExternalId?: string;
  paymentAccount?: string;
  paymentProvider?: PaymentProvider;
  constraintDisplayNameMapping?: Record<string, string[] | null>;
}

async function getClaimDebuggingContextQuery({
  claimId,
}: {
  claimId: string;
}): Promise<BackResponse<ClaimDebuggingContext> | BackResponseFailure> {
  try {
    const response = await apiCall<BackResponse<ClaimDebuggingContext>>(
      ENDPOINT.replace(COMMON_QUERY_PARAMS.CLAIM_ID, claimId),
      { method: SUPPORTED_METHODS.GET },
    );

    return response;
  } catch (exception) {
    return prepareResponseFailure(exception);
  }
}

export default getClaimDebuggingContextQuery;
